// src/components/organisms/MainToolbar/MainToolbar.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Tooltip,
  useTheme,
  useMediaQuery,
  IconButton,
  ClickAwayListener
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  YouTube as YouTubeIcon,
  Instagram as InstagramIcon,
  X as XIcon,
  LinkedIn as LinkedInIcon,
  Language as WebIcon,
  Article as ArticleIcon,
  VideoLibrary as VideoIcon,
  SmartDisplay as ShortIcon,
  PhotoLibrary as ReelIcon,
  Image as PostIcon,
  AutoStories as StoryIcon,
  Newspaper as NewspaperIcon,
  MenuBook as MagazineIcon,
  Tv as TvIcon,
  Radio as RadioIcon,
  Podcasts as PodcastIcon,
  SentimentSatisfied,
  SentimentNeutral,
  SentimentDissatisfied,
  Star as StarIcon,
  Devices as PlatformIcon,
  Category as FormatIcon,
  Dashboard as DashboardIcon,
  LocalOffer as LocalOfferIcon,
  CollectionsBookmark as CollectionsBookmarkIcon,
  Assignment as BriefingIcon,
  Mood as SentimentIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import SearchBar from '../../molecules/SearchBar/SearchBar';
import SelectionToggle from '../../molecules/SelectionToggle/SelectionToggle';
import FilterButton from '../../molecules/FilterButton';
import SortSelector from '../../molecules/SortSelector/SortSelector';
import ViewModeSelector from '../../molecules/ViewModeSelector/ViewModeSelector';
import ItemsPerPageSelector from '../../molecules/ItemsPerPageSelector/ItemsPerPageSelector';
import PeriodFilterButton from '../../molecules/PeriodFilterButton/PeriodFilterButton';
import QuickFacetFilterButton from '../../molecules/QuickFacetFilterButton/QuickFacetFilterButton';
import ToolbarPrimaryActionButton from '../../molecules/ToolbarPrimaryActionButton';
import {
  getOperationalMode,
  operationalComponentSurfaces,
  operationalRadii,
  operationalSizing
} from '../../../design-system/tokens';

// Ícone customizado para TikTok
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

// Ícone customizado para Facebook
const FacebookIcon = (props) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" {...props}>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

// Ícone customizado para Pinterest
const PinterestIcon = (props) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" {...props}>
    <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z" />
  </svg>
);

/**
 * MainToolbar - Componente organismo para a toolbar principal
 *
 * CLASSIFICAÇÃO: ORGANISMO
 *
 * Este componente combina diversos componentes moleculares e atômicos
 * para formar a toolbar principal da aplicação. Inclui busca, filtros,
 * seleção, ordenação e visualização.
 *
 * @see https://bradfrost.com/blog/post/atomic-web-design/
 */
const MainToolbar = ({
  // Título da página (opcional)
  title,
  // Subtítulo (opcional) - pode ser string ou JSX
  subtitle,
  // Tipo de icone (opcional) para contornar titulos dinamicos
  iconType,

  searchValue,
  onSearch,
  selectionMode,
  onToggleSelectionMode,
  filtersOpen,
  onToggleFilters,
  activeFiltersCount = 0,
  sortBy,
  onSortChange,
  sortOptions,
  variant,
  onVariantChange,
  cardsPerPage,
  onCardsPerPageChange,

  // Novas props para estatísticas
  filteredItems = 0,
  selectedItems = 0,

  // Props para filtro de período
  timePeriod = '24h',
  onTimePeriodChange,
  totalCount = 0,
  customDateRange = null, // { startDate, endDate, dateType } — datas já setadas
  // Tipo de data controlado externamente (igual ao toggle do Personalizado)
  quickDateType = 'created',
  onQuickDateTypeChange,

  // Props para Quick Filters (dropdowns)
  selectedMediaTypes = [],
  onMediaTypesChange,
  mediaTypeOptions = [],
  selectedContentTypes = [],
  onContentTypesChange,
  contentTypeOptions = [],
  selectedTiers = [],
  onTiersChange,
  tierOptions = [],
  selectedSentiments = [],
  onSentimentsChange,
  sentimentOptions = [],

  // Filtro de Tipo de Marca (Minha Marca / Concorrentes)
  selectedEntityType = '',
  onEntityTypeChange,
  entityTypeOptions = [],

  // Props para filtros facetados (contagens dinâmicas)
  facets = null, // { mediaType: {YouTube: 10, ...}, sentiment: {...}, ... }

  // Novas props para ações
  onNewArticle,
  compactSearch = false,

  className = '',
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchExpanded, setSearchExpanded] = useState(false);
  const isSearchOpen = !compactSearch || searchExpanded || Boolean(searchValue);
  const chipTokens = operationalComponentSurfaces.chip[getOperationalMode(theme.palette.mode)];
  const sectionIcon = (() => {
    const iconSx = { mr: 1.15, color: 'text.primary', fontSize: compactSearch ? 26 : 24, opacity: 0.96 };
    if (iconType === 'tag') return <LocalOfferIcon sx={iconSx} />;
    if (iconType === 'collection') return <CollectionsBookmarkIcon sx={iconSx} />;
    if (iconType === 'favorite') return <StarIcon sx={iconSx} />;
    if (iconType === 'article') return <ArticleIcon sx={iconSx} />;

    if (title?.includes('Artigos')) return <ArticleIcon sx={iconSx} />;
    if (title?.includes('Tags')) return <LocalOfferIcon sx={iconSx} />;
    if (title?.includes('Coleções')) return <CollectionsBookmarkIcon sx={iconSx} />;
    if (title?.includes('Favoritos')) return <StarIcon sx={iconSx} />;
    if (title?.includes('Dashboard')) return <DashboardIcon sx={iconSx} />;
    if (title?.includes('Briefing')) return <BriefingIcon sx={iconSx} />;
    return null;
  })();
  const sectionTitle = title ? (
    <Typography
      variant={compactSearch ? 'subtitle1' : 'h5'}
      sx={{
        fontWeight: compactSearch ? 800 : 600,
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        color: 'text.primary',
        fontSize: compactSearch ? '0.98rem' : undefined,
        letterSpacing: 0,
        lineHeight: 1.2
      }}
    >
      {sectionIcon}
      <span translate="no">{title}</span>
    </Typography>
  ) : null;

  // Ícones para cada plataforma
  const getPlatformIcon = (value) => {
    const icons = {
      'youtube': YouTubeIcon,
      'tiktok': TikTokIcon,
      'instagram': InstagramIcon,
      'x': XIcon,
      'twitter': XIcon,
      'facebook': FacebookIcon,
      'linkedin': LinkedInIcon,
      'pinterest': PinterestIcon,
      'web': WebIcon,
      'article': ArticleIcon,
      'jornal': NewspaperIcon,
      'revista': MagazineIcon,
      'tv': TvIcon,
      'radio': RadioIcon,
      'podcast': PodcastIcon
    };
    return icons[value.toLowerCase()] || WebIcon;
  };

  // Ícones para cada formato
  const getFormatIcon = (value) => {
    const icons = {
      'video': VideoIcon,
      'short': ShortIcon,
      'reel': ReelIcon,
      'story': StoryIcon,
      'post': PostIcon,
      'pin': PinterestIcon,
      'article': ArticleIcon
    };
    return icons[value.toLowerCase()] || ArticleIcon;
  };

  // Ícones para sentimentos
  const getSentimentIcon = (value) => {
    const icons = {
      'positive': SentimentSatisfied,
      'neutral': SentimentNeutral,
      'negative': SentimentDissatisfied
    };
    return icons[value.toLowerCase()] || SentimentNeutral;
  };

  // Helper para obter contagem de facetas
  const getFacetCount = (filterType, value) => {
    if (!facets || !facets[filterType]) return null;
    return facets[filterType][value] ?? null;
  };

  // Helper para verificar se opção deve ser desabilitada
  const isOptionDisabled = (filterType, value, currentSelection) => {
    // Se já está selecionado, nunca desabilita (para poder desselecionar)
    if (currentSelection.includes(value)) return false;
    // Se não temos facets, não desabilita
    if (!facets || !facets[filterType]) return false;
    // Se count é 0, desabilita
    const count = facets[filterType][value];
    return count === 0;
  };

  if (compactSearch) {
    return (
      <Box
        className={`main-toolbar ${className}`}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 0.8,
          minWidth: 0,
          flexWrap: 'nowrap'
        }}
        {...props}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.15, minWidth: 0, flexShrink: 1 }}>
          {sectionTitle}
          {!isMobile && (totalCount > 0 || filteredItems > 0) && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                whiteSpace: 'nowrap',
                fontSize: '0.78rem',
                fontWeight: 500,
                letterSpacing: 0,
                lineHeight: 1.2,
                display: { xs: 'none', lg: 'block' }
              }}
            >
              <Box component="strong" sx={{ color: 'text.primary', fontWeight: 800 }}>{filteredItems}</Box> de <Box component="strong" sx={{ color: 'text.primary', fontWeight: 800 }}>{totalCount > 0 ? totalCount : filteredItems}</Box>
            </Typography>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 12 }} />

        <ClickAwayListener onClickAway={() => {
          if (!searchValue) setSearchExpanded(false);
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {isSearchOpen ? (
              <SearchBar
                value={searchValue}
                onChange={onSearch}
                placeholder="Pesquisar artigos..."
                inputProps={{ translate: 'no', autoFocus: searchExpanded }}
                sx={{ minWidth: 260, maxWidth: 320 }}
              />
            ) : (
              <Tooltip title="Pesquisar artigos" arrow>
                <IconButton
                  size="small"
                  onClick={() => setSearchExpanded(true)}
                  sx={{
                    width: operationalSizing.compact,
                    height: operationalSizing.compact,
                    borderRadius: `${operationalRadii.control}px`,
                    bgcolor: chipTokens.background,
                    color: theme.palette.text.primary,
                    '&:hover': {
                      bgcolor: chipTokens.backgroundHover
                    }
                  }}
                >
                  <SearchIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </ClickAwayListener>

        {onTimePeriodChange && (
          <PeriodFilterButton
            value={timePeriod}
            onChange={onTimePeriodChange}
            customDateRange={customDateRange}
            quickDateType={quickDateType}
            onQuickDateTypeChange={onQuickDateTypeChange}
          />
        )}

        <FilterButton
          active={filtersOpen}
          onClick={onToggleFilters}
          activeFiltersCount={activeFiltersCount}
          tooltip="Filtros avancados"
        />

        {!selectionMode && (
          <SelectionToggle
            active={selectionMode}
            onClick={onToggleSelectionMode}
            selectedCount={selectedItems}
          />
        )}

        <SortSelector
          value={sortBy}
          onChange={onSortChange}
          options={sortOptions}
        />

        <ViewModeSelector
          value={variant}
          onChange={onVariantChange}
        />

        {cardsPerPage && onCardsPerPageChange && (
          <Box sx={{ display: 'none' }}>
            <ItemsPerPageSelector
              value={cardsPerPage}
              onChange={onCardsPerPageChange}
            />
          </Box>
        )}
      </Box>
    );
  }


  return (
    <Box
      className={`main-toolbar ${className}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        background: 'transparent',
        p: 0,
        flexWrap: compactSearch ? 'nowrap' : 'wrap'
      }}
      {...props}
    >
      {/* Título e Subtítulo da página (se fornecidos) */}
      {!compactSearch && (title || subtitle) && (
        <Box sx={{ flexShrink: 0 }}>
          {title && (
            <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              {(() => {
                if (iconType === 'tag') return <LocalOfferIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 32 }} />;
                if (iconType === 'collection') return <CollectionsBookmarkIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 32 }} />;
                if (iconType === 'favorite') return <StarIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 32 }} />;
                if (iconType === 'article') return <ArticleIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 32 }} />;

                if (title.includes('Artigos')) return <ArticleIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 32 }} />;
                if (title.includes('Tags')) return <LocalOfferIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 32 }} />;
                if (title.includes('Coleções')) return <CollectionsBookmarkIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 32 }} />;
                if (title.includes('Favoritos')) return <StarIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 32 }} />;
                if (title.includes('Dashboard')) return <DashboardIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 32 }} />;
                if (title.includes('Briefing')) return <BriefingIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 32 }} />;
                return null;
              })()}
              <span translate="no">{title}</span>
            </Typography>
          )}
          {subtitle && (
            typeof subtitle === 'string' ? (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            ) : (
              subtitle
            )
          )}
        </Box>
      )}

      {/* Seção Esquerda: Busca, Seleção e Filtros */}
      {compactSearch && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.45, minWidth: 0, flexShrink: 0 }}>
          {sectionTitle}
          {!isMobile && (totalCount > 0 || filteredItems > 0) && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                whiteSpace: 'nowrap',
                fontSize: '0.98rem',
                fontWeight: 500,
                letterSpacing: 0,
                lineHeight: 1.2
              }}
            >
              Mostrando <Box component="strong" sx={{ color: 'text.primary', fontWeight: 800 }}>{filteredItems}</Box> de <Box component="strong" sx={{ color: 'text.primary', fontWeight: 800 }}>{totalCount > 0 ? totalCount : filteredItems}</Box> <span translate="no">artigos</span>
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexGrow: 1,
        flexWrap: compactSearch ? 'nowrap' : 'wrap',
        justifyContent: compactSearch ? 'flex-end' : 'flex-start',
        minWidth: 0
      }}>
        <ClickAwayListener onClickAway={() => {
          if (compactSearch && !searchValue) {
            setSearchExpanded(false);
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {isSearchOpen ? (
              <SearchBar
                value={searchValue}
                onChange={onSearch}
                placeholder="Pesquisar artigos..."
                inputProps={{ translate: 'no', autoFocus: compactSearch && searchExpanded }}
                sx={{ minWidth: compactSearch ? 260 : 200, maxWidth: compactSearch ? 320 : 300 }}
              />
            ) : (
              <Tooltip title="Pesquisar artigos" arrow>
                <IconButton
                  size="small"
                  onClick={() => setSearchExpanded(true)}
                  sx={{
                    width: operationalSizing.compact,
                    height: operationalSizing.compact,
                    borderRadius: `${operationalRadii.control}px`,
                    bgcolor: chipTokens.background,
                    color: theme.palette.text.primary,
                    '&:hover': {
                      bgcolor: chipTokens.backgroundHover
                    }
                  }}
                >
                  <SearchIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </ClickAwayListener>

        {/* Dropdown de Período */}
        {onTimePeriodChange && (
          <PeriodFilterButton
            value={timePeriod}
            onChange={onTimePeriodChange}
            customDateRange={customDateRange}
            quickDateType={quickDateType}
            onQuickDateTypeChange={onQuickDateTypeChange}
          />
        )}

        <FilterButton
          active={filtersOpen}
          onClick={onToggleFilters}
          activeFiltersCount={activeFiltersCount}
        />

        <QuickFacetFilterButton
          tooltip="Plataformas"
          icon={<PlatformIcon />}
          options={mediaTypeOptions}
          selectedValues={selectedMediaTypes}
          onChange={onMediaTypesChange}
          getOptionIcon={getPlatformIcon}
          getOptionCount={(value) => getFacetCount('mediaType', value)}
          isOptionDisabled={(value, option, currentSelection) => isOptionDisabled('mediaType', value, currentSelection)}
          menuMinWidth={180}
        />

        <QuickFacetFilterButton
          tooltip="Formato"
          icon={<FormatIcon />}
          options={contentTypeOptions}
          selectedValues={selectedContentTypes}
          onChange={onContentTypesChange}
          getOptionIcon={getFormatIcon}
          getOptionCount={(value) => getFacetCount('contentType', value)}
          isOptionDisabled={(value, option, currentSelection) => isOptionDisabled('contentType', value, currentSelection)}
          menuMinWidth={160}
        />

        <QuickFacetFilterButton
          tooltip="Tier"
          icon={<StarIcon />}
          options={tierOptions}
          selectedValues={selectedTiers}
          onChange={onTiersChange}
          getOptionIcon={() => StarIcon}
          getOptionCount={(value) => getFacetCount('tier', value)}
          isOptionDisabled={(value, option, currentSelection) => isOptionDisabled('tier', value, currentSelection)}
          menuMinWidth={140}
        />

        <QuickFacetFilterButton
          tooltip="Sentimento"
          icon={<SentimentIcon />}
          options={sentimentOptions}
          selectedValues={selectedSentiments}
          onChange={onSentimentsChange}
          getOptionIcon={getSentimentIcon}
          getOptionCount={(value) => getFacetCount('sentiment', value)}
          isOptionDisabled={(value, option, currentSelection) => isOptionDisabled('sentiment', value, currentSelection)}
          menuMinWidth={160}
        />

        {/* Filtro de Tipo de Marca — só aparece se há opções (marca configurada) */}
        {entityTypeOptions?.length > 0 && (
          <QuickFacetFilterButton
            tooltip="Tipo de Marca"
            icon={<BusinessIcon />}
            options={entityTypeOptions}
            selectedValues={selectedEntityType ? [selectedEntityType] : []}
            onChange={(vals) => {
              // toggle: clicar na opção já selecionada desmarca
              const next = vals[vals.length - 1] || '';
              onEntityTypeChange?.(next === selectedEntityType ? '' : next);
            }}
            getOptionIcon={() => BusinessIcon}
            menuMinWidth={180}
          />
        )}

        {/* Botão de seleção - só aparece quando NÃO está em modo seleção */}
        {!selectionMode && (
          <SelectionToggle
            active={selectionMode}
            onClick={onToggleSelectionMode}
            selectedCount={selectedItems}
          />
        )}
      </Box>

      {/* Texto "Mostrando X de Y artigos" */}
      {!compactSearch && !isMobile && (totalCount > 0 || filteredItems > 0) && (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          Mostrando <strong>{filteredItems}</strong> de <strong>{totalCount > 0 ? totalCount : filteredItems}</strong> <span translate="no">artigos</span>
        </Typography>
      )}

      {/* Seção Direita: Controles */}
      <Box sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        flexWrap: compactSearch ? 'nowrap' : 'wrap',
        flexShrink: 0
      }}>
        {/* Ordenação */}
        <SortSelector
          value={sortBy}
          onChange={onSortChange}
          options={sortOptions}
        />

        {/* Modo de Visualização */}
        <ViewModeSelector
          value={variant}
          onChange={onVariantChange}
        />

        {/* Mantido montado por compatibilidade com a paginação legada, mas oculto da UI. */}
        {cardsPerPage && onCardsPerPageChange && (
          <Box sx={{ display: 'none' }}>
            <ItemsPerPageSelector
              value={cardsPerPage}
              onChange={onCardsPerPageChange}
            />
          </Box>
        )}

        {/* Botão Novo Artigo */}
        {onNewArticle && (
          <ToolbarPrimaryActionButton
            tooltip="Adicionar novo artigo"
            label="Artigos"
            icon={<AddIcon />}
            onClick={onNewArticle}
          />
        )}
      </Box>

    </Box>
  );
};

MainToolbar.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  searchValue: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  selectionMode: PropTypes.bool.isRequired,
  onToggleSelectionMode: PropTypes.func.isRequired,
  filtersOpen: PropTypes.bool.isRequired,
  onToggleFilters: PropTypes.func.isRequired,
  activeFiltersCount: PropTypes.number,
  sortBy: PropTypes.string.isRequired,
  onSortChange: PropTypes.func.isRequired,
  sortOptions: PropTypes.array,
  variant: PropTypes.oneOf(['compact', 'detailed', 'list', 'measurement']).isRequired,
  onVariantChange: PropTypes.func.isRequired,
  cardsPerPage: PropTypes.number,
  onCardsPerPageChange: PropTypes.func,

  // Novas props para estatísticas
  totalItems: PropTypes.number,
  filteredItems: PropTypes.number,
  selectedItems: PropTypes.number,

  // Props para filtro de período
  timePeriod: PropTypes.string,
  onTimePeriodChange: PropTypes.func,
  totalCount: PropTypes.number,
  quickDateType: PropTypes.oneOf(['published', 'created']),
  onQuickDateTypeChange: PropTypes.func,

  // Props para Quick Filters
  selectedMediaTypes: PropTypes.array,
  onMediaTypesChange: PropTypes.func,
  mediaTypeOptions: PropTypes.array,
  selectedContentTypes: PropTypes.array,
  onContentTypesChange: PropTypes.func,
  contentTypeOptions: PropTypes.array,
  selectedTiers: PropTypes.array,
  onTiersChange: PropTypes.func,
  tierOptions: PropTypes.array,
  selectedSentiments: PropTypes.array,
  onSentimentsChange: PropTypes.func,
  sentimentOptions: PropTypes.array,
  selectedEntityType: PropTypes.string,
  onEntityTypeChange: PropTypes.func,
  entityTypeOptions: PropTypes.array,

  // Props para filtros facetados
  facets: PropTypes.object,
  facetsLoading: PropTypes.bool,

  // Novas props para ações
  onExport: PropTypes.func,
  onRefresh: PropTypes.func,
  onSettings: PropTypes.func,
  onNewArticle: PropTypes.func,
  compactSearch: PropTypes.bool,

  className: PropTypes.string
};

// Valores padrão para as opções de ordenação
MainToolbar.defaultProps = {
  sortOptions: [
    { value: 'date_desc', label: 'Publicação Recente' },
    { value: 'date_asc', label: 'Publicação Antiga' },
    { value: 'created_desc', label: 'Cadastro Recente' },
    { value: 'created_asc', label: 'Cadastro Antigo' },
    { value: 'valuation_desc', label: 'Maior Valoração' },
    { value: 'valuation_asc', label: 'Menor Valoração' },
    { value: 'tier_asc', label: 'Tier 1 Primeiro' },
    { value: 'tier_desc', label: 'Tier 3 Primeiro' },
    { value: 'title_az', label: 'Título A-Z' },
    { value: 'title_za', label: 'Título Z-A' },
    { value: 'source_az', label: 'Fonte A-Z' },
    { value: 'source_za', label: 'Fonte Z-A' },
    { value: 'sentiment', label: 'Por Sentimento' },
    { value: 'favorite', label: 'Favoritos Primeiro' },
    { value: 'unread', label: 'Não Lidos Primeiro' }
  ]
};

export default MainToolbar;
